import React from 'react';
import classNames from 'classnames';
import { TooltipContext } from '#modules/context/TooltipContext';
import styles from './Tooltip.scss';

// Arrow state constants
const ARROW_UP = 1;
const ARROW_DOWN = 2;
const ARROW_LEFT = 3;
const ARROW_RIGHT = 4;

// Tooltip position states (relative to target)
const TOOLTIP_N = 'north';
const TOOLTIP_NE = 'northeast';
const TOOLTIP_E = 'east';
const TOOLTIP_SE = 'southeast';
const TOOLTIP_S = 'south';
const TOOLTIP_SW = 'southwest';
const TOOLTIP_W = 'west';
const TOOLTIP_NW = 'northwest';

// Viewport config
const VIEWPORT_EDGE_THRESHOLD_PX = 150; // Minimum px between the DOM element and viewport edge
const VIEWPORT_EDGE_THRESHOLD_PCT = 10; // Minimum viewport % between the DOM element and viewport edge

// Default distance between the tooltip's edge and arrow
const arrowOffsetPx = 20;
// Size of the arrow
const arrowWidthPx = 8;

const findTooltipLocation = ({ parentPosition, maxWidth }) => {
  const { innerWidth: viewportWidth, innerHeight: viewportHeight } = window;

  const viewportBottomEdgeDistance = viewportHeight - parentPosition.bottom;
  const parentViewportTopPct = Math.round(
    (parentPosition.top * 100) / viewportHeight,
  );
  const parentViewportBottomPct = Math.round(
    (parentPosition.top * 100) / viewportHeight,
  );
  const parentHalfWidth = Math.round(
    (parentPosition.right - parentPosition.left) / 2,
  );
  let tooltipLeft = parentPosition.left + parentHalfWidth;
  let tooltipLocation = parentViewportTopPct < 50 ? TOOLTIP_S : TOOLTIP_N;

  // Check available space to the left
  if (tooltipLeft < maxWidth) {
    tooltipLocation = TOOLTIP_E;
    if (
      parentViewportTopPct <= VIEWPORT_EDGE_THRESHOLD_PCT ||
      parentPosition.top < VIEWPORT_EDGE_THRESHOLD_PX
    ) {
      tooltipLocation = TOOLTIP_SE;
    } else if (
      parentViewportBottomPct > 100 - VIEWPORT_EDGE_THRESHOLD_PCT ||
      viewportBottomEdgeDistance < VIEWPORT_EDGE_THRESHOLD_PX
    ) {
      tooltipLocation = TOOLTIP_NE;
    }
  }
  // Check available space to the right
  else if (viewportWidth - tooltipLeft < maxWidth) {
    tooltipLocation = TOOLTIP_W;
    if (
      parentViewportTopPct <= VIEWPORT_EDGE_THRESHOLD_PCT ||
      parentPosition.top < VIEWPORT_EDGE_THRESHOLD_PX
    ) {
      tooltipLocation = TOOLTIP_SW;
    } else if (
      parentViewportBottomPct > 100 - VIEWPORT_EDGE_THRESHOLD_PCT ||
      viewportBottomEdgeDistance < VIEWPORT_EDGE_THRESHOLD_PX
    ) {
      tooltipLocation = TOOLTIP_NW;
    }
  }

  return tooltipLocation;
};

const getPositionForTooltipLocation = ({ parentPosition, tooltipLocation }) => {
  const parentHalfWidth = Math.round(
    (parentPosition.right - parentPosition.left) / 2,
  );
  const parentHalfHeight = Math.round(
    (parentPosition.bottom - parentPosition.top) / 2,
  );

  let tooltipTop;
  let tooltipLeft;
  let arrowDirection;

  switch (tooltipLocation) {
    case TOOLTIP_N:
      arrowDirection = ARROW_DOWN;
      tooltipTop = parentPosition.top - arrowOffsetPx / 2;
      tooltipLeft = parentPosition.left + parentHalfWidth;
      break;
    case TOOLTIP_NE:
      arrowDirection = ARROW_DOWN;
      tooltipTop = parentPosition.top - arrowOffsetPx / 2;
      tooltipLeft = parentPosition.left + parentHalfWidth - arrowOffsetPx + 2;
      break;
    case TOOLTIP_E:
      arrowDirection = ARROW_LEFT;
      tooltipTop = parentPosition.bottom - parentHalfHeight;
      tooltipLeft = parentPosition.right + arrowWidthPx + 1;
      break;
    case TOOLTIP_SE:
      arrowDirection = ARROW_UP;
      tooltipTop = parentPosition.bottom + arrowOffsetPx / 2;
      tooltipLeft = parentPosition.left - arrowOffsetPx - 2;
      break;
    case TOOLTIP_SW:
      arrowDirection = ARROW_UP;
      tooltipTop = parentPosition.bottom + arrowOffsetPx / 2;
      tooltipLeft = parentPosition.left + parentHalfWidth + arrowOffsetPx;
      break;
    case TOOLTIP_W:
      arrowDirection = ARROW_RIGHT;
      tooltipTop = parentPosition.top + parentHalfHeight;
      tooltipLeft = parentPosition.left - arrowOffsetPx / 2;
      break;
    case TOOLTIP_NW:
      arrowDirection = ARROW_DOWN;
      tooltipTop = parentPosition.top - arrowOffsetPx / 2;
      tooltipLeft = parentPosition.left + parentHalfWidth + arrowOffsetPx;
      break;
    default:
    case TOOLTIP_S:
      arrowDirection = ARROW_UP;
      tooltipTop = parentPosition.bottom + arrowOffsetPx / 2;
      tooltipLeft = parentPosition.left + parentHalfWidth;
      break;
  }

  return {
    arrowDirection,
    tooltipTop,
    tooltipLeft,
  };
};

const showTooltip = ({
  setVisible,
  setPosition,
  maxWidth,
  location,
  relativeToRef,
  hideTooltip,
  setHideActiveTooltip,
}) => {
  if (!relativeToRef) {
    return;
  }

  // Show tooltip frame
  setVisible(true);

  // Store hide action relevant to the currently visible tooltip
  setHideActiveTooltip(() => hideTooltip);

  // Find wrapped target's position in the viewport
  const parentPosition = relativeToRef.current.getBoundingClientRect();

  // Calculate location unless manually predetermined
  const tooltipLocation =
    location ||
    findTooltipLocation({
      parentPosition,
      maxWidth,
    });

  const { tooltipTop, tooltipLeft, arrowDirection } =
    getPositionForTooltipLocation({ parentPosition, tooltipLocation });

  setPosition({
    top: tooltipTop,
    left: tooltipLeft,
    arrowDirection,
    tooltipLocation,
  });
};

export default function Tooltip({
  text,
  children,
  wrapperClassName,
  targetClassName,
  className,
  maxWidth = 200,
  location,
  noWrap,
  leftAlign,
  alwaysVisible,
  disabled,
}) {
  const [visible, setVisible] = React.useState(false);
  const { setHideActiveTooltip, tooltipDisabled } =
    React.useContext(TooltipContext);
  const [position, setPosition] = React.useState({
    top: 0,
    left: 0,
    arrowDirection: ARROW_UP,
    tooltipLocation: location || TOOLTIP_S,
  });
  const ref = React.useRef(null);
  const hideTooltip = () => setVisible(false);

  if (disabled) {
    return children;
  }

  return (
    <div
      className={classNames(styles.TooltipWrapper, wrapperClassName)}
      ref={ref}
    >
      <div
        className={classNames(className, styles.Tooltip, {
          [styles.visible]: alwaysVisible || visible,
          [styles.centeredX]: [TOOLTIP_N, TOOLTIP_S].includes(
            position.tooltipLocation,
          ),
          [styles.centeredY]: [TOOLTIP_E, TOOLTIP_W].includes(
            position.tooltipLocation,
          ),
          [styles.reversedX]: [TOOLTIP_NW, TOOLTIP_SW, TOOLTIP_W].includes(
            position.tooltipLocation,
          ),
          [styles.reversedY]: [TOOLTIP_NW, TOOLTIP_N, TOOLTIP_NE].includes(
            position.tooltipLocation,
          ),
          [styles.leftAlign]: leftAlign,
          [styles.noWrap]: noWrap,
        })}
        style={{
          maxWidth: `${maxWidth}px`,
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
      >
        <div
          className={classNames(styles.arrow, {
            [styles.down]: position.arrowDirection === ARROW_DOWN,
            [styles.left]: position.arrowDirection === ARROW_LEFT,
            [styles.right]: position.arrowDirection === ARROW_RIGHT,
            [styles.end]: [TOOLTIP_SW, TOOLTIP_NW].includes(
              position.tooltipLocation,
            ),
            [styles.centeredX]: [TOOLTIP_S, TOOLTIP_N].includes(
              position.tooltipLocation,
            ),
            [styles.centeredY]: [TOOLTIP_E, TOOLTIP_W].includes(
              position.tooltipLocation,
            ),
          })}
        />
        <div className={styles.body}>{text || ''}</div>
      </div>
      <div
        className={classNames(styles.target, targetClassName)}
        role="button"
        tabIndex={0}
        onMouseEnter={() =>
          !!text &&
          !tooltipDisabled &&
          showTooltip({
            setVisible,
            setPosition,
            maxWidth,
            location,
            relativeToRef: ref,
            hideTooltip,
            setHideActiveTooltip,
          })
        }
        onMouseLeave={hideTooltip}
        onClick={hideTooltip}
        onKeyPress={hideTooltip}
      >
        {children}
      </div>
    </div>
  );
}
